export const SUPPORTED_LANGUAGES = ['kz', 'ru', 'en'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'kz';

export const LANGUAGE_PARAM = ':lng';

const SUPPORTED_LANGUAGES_SET = new Set<string>(SUPPORTED_LANGUAGES);

export const isSupportedLanguage = (
  language: string | null | undefined
): language is SupportedLanguage =>
  typeof language === 'string' && SUPPORTED_LANGUAGES_SET.has(language);

export const isDefaultLanguage = (
  language: string | null | undefined
): language is SupportedLanguage =>
  typeof language === 'string' && language === DEFAULT_LANGUAGE;

const ensureLeadingSlash = (path: string): string =>
  path.startsWith('/') ? path : `/${path}`;

const shouldPrefixLanguage = (language: SupportedLanguage): boolean =>
  language !== DEFAULT_LANGUAGE;

const normalizePath = (path: string): string => {
  if (!path || path === '/') {
    return '/';
  }

  return ensureLeadingSlash(path);
};

export const buildLocalizedPaths = (path: string): string[] => {
  if (path === '/' || path === '') {
    return ['/', `/${LANGUAGE_PARAM}`];
  }

  const normalizedPath = ensureLeadingSlash(path);

  return [normalizedPath, `/${LANGUAGE_PARAM}${normalizedPath}`];
};

export const extractLanguageFromPathname = (
  pathname: string
): SupportedLanguage | null => {
  const [, firstSegment] = pathname.split('/');

  if (isSupportedLanguage(firstSegment)) {
    return firstSegment;
  }

  return null;
};

export const removeLanguageFromPathname = (pathname: string): string => {
  if (!pathname || pathname === '/') {
    return '/';
  }

  const segments = pathname.split('/');
  const firstSegment = segments[1];

  if (!isSupportedLanguage(firstSegment)) {
    return pathname;
  }

  const rest = segments.slice(2).join('/');

  return rest ? `/${rest}` : '/';
};

export const applyLanguageToPathname = (
  pathname: string,
  language: SupportedLanguage
): string => {
  if (pathname.startsWith('http')) {
    return pathname;
  }

  const normalizedPath = removeLanguageFromPathname(pathname);

  if (!shouldPrefixLanguage(language)) {
    return normalizedPath;
  }

  if (normalizedPath === '/') {
    return `/${language}`;
  }

  return `/${language}${normalizedPath}`;
};

export const prefixPathWithLanguage = (
  path: string,
  language: SupportedLanguage
): string => {
  const normalizedPath = normalizePath(path);

  if (!shouldPrefixLanguage(language)) {
    return normalizedPath;
  }

  if (normalizedPath === '/') {
    return `/${language}`;
  }

  return `/${language}${normalizedPath}`;
};

export const readPreferredLanguage = (): SupportedLanguage => {
  const storedLanguage = localStorage.getItem('language');

  if (isSupportedLanguage(storedLanguage)) {
    return storedLanguage;
  }

  return DEFAULT_LANGUAGE;
};
